import React, { useState, useMemo, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BsCloudUpload } from "react-icons/bs";
import { MdDeleteForever } from "react-icons/md";
import { GrFormAdd } from "react-icons/gr";
import { FiSearch, FiInfo } from "react-icons/fi";
import ActionButton from "../../../Common/ActionButton";
import { InputField, SelectField } from "../../../Common/FormComponents";
import { getallCategory } from "../../../../Redux/actions/Operationsactions/getcategory";
import { getallMaterial } from "../../../../Redux/actions/Operationsactions/getmaterial";
import { getRateLibraryRecords, saveRateLibraryRecord } from "./rateLibraryStorage";

const UOM_BASE_OPTIONS = [
  { value: "Per 1 Cft", label: "Per 1 Cft" },
  { value: "Per 100 Cft", label: "Per 100 Cft" },
  { value: "Per 1 Sft", label: "Per 1 Sft" },
  { value: "Per 100 Sft", label: "Per 100 Sft" },
  { value: "Per Ton", label: "Per Ton" },
  { value: "Per Bag", label: "Per Bag" },
  { value: "Per Kg", label: "Per Kg" },
  { value: "Per Nos", label: "Per Nos" },
];

const emptyMaterialRow = () => ({
  id: Date.now() + Math.random(),
  MaterialSKU: "",
  MaterialName: "",
  MaterialCategory: "",
  Coefficient: "",
  BaseUOM: "",
  AllowableWastage: "0",
});

const headerCellStyle = {
  height: "42px",
  paddingTop: "6px",
  paddingBottom: "6px",
  verticalAlign: "middle",
  lineHeight: "1.1",
  fontWeight: "600",
  fontSize: "12px",
  color: "#333",
};

/**
 * RateLibraryForm Screen (Full-page form, matching project theme)
 */
const RateLibraryForm = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams();

  const isEdit = Boolean(id);

  // Redux store data
  const categoryStore = useSelector((s) => s.getAllCategory?.data || []);
  const materialStore = useSelector((s) => s.getAllMaterial?.data || []);

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    ActivityName: "",
    UOMBase: "Per 100 Cft",
    Category: "",
    UnitEstimateRate: "12000",
  });

  const [lineItems, setLineItems] = useState([emptyMaterialRow()]);
  const [activeSearchIdx, setActiveSearchIdx] = useState(null);
  const [searchFilter, setSearchFilter] = useState({});

  useEffect(() => {
    dispatch(getallCategory());
    dispatch(getallMaterial());
  }, [dispatch]);

  // Load existing record if in edit mode
  useEffect(() => {
    if (id) {
      const records = getRateLibraryRecords();
      const match = records.find((r) => String(r.Id) === String(id));
      if (match) {
        setFormData({
          ActivityName: match.ActivityName || "",
          UOMBase: match.UOMBase || "Per 100 Cft",
          Category: match.Category || "",
          UnitEstimateRate: match.UnitEstimateRate || "",
        });
        if (match.MaterialRows && match.MaterialRows.length > 0) {
          setLineItems(
            match.MaterialRows.map((r) => ({
              ...emptyMaterialRow(),
              ...r,
            })),
          );
        }
      }
    }
  }, [id]);

  // Format Category options from store + fallbacks
  const categoryOptions = useMemo(() => {
    const fromStore = (categoryStore || [])
      .map((c) => ({
        value: c.CategoryName || c.Name || c.name || "",
        label: c.CategoryName || c.Name || c.name || "",
      }))
      .filter((c) => c.value);

    const uniqueMap = new Map();
    [...fromStore].forEach((item) => {
      if (!uniqueMap.has(item.value)) {
        uniqueMap.set(item.value, item);
      }
    });

    return [{ value: "", label: "Select Category" }, ...Array.from(uniqueMap.values())];
  }, [categoryStore]);

  // Format Material options from store + fallbacks
  const materialList = useMemo(() => {
    const fromStore = (materialStore || []).map((m) => ({
      name: m.ItemName || "",
      category: m.CategoryName || "",
      uom: m.UomName || "",
    }));

    return fromStore;
  }, [materialStore]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRow = () => {
    setLineItems((prev) => [...prev, emptyMaterialRow()]);
  };

  const handleRemoveRow = (idx) => {
    if (lineItems.length === 1) {
      toast.warn("At least one material row is required.");
      return;
    }
    setLineItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleLineChange = (idx, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleSelectMaterial = (idx, mat) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        MaterialSKU: mat.sku,
        MaterialName: mat.name,
        MaterialCategory: mat.category,
        BaseUOM: mat.uom,
      };
      return updated;
    });
    setActiveSearchIdx(null);
    setSearchFilter((prev) => ({ ...prev, [idx]: "" }));
  };

  const calculateTotalWithWastage = (item) => {
    const coeff = parseFloat(item.Coefficient) || 0;
    const wastage = parseFloat(item.AllowableWastage) || 0;
    if (!coeff) {
      return "—";
    }
    const total = coeff * (1 + wastage / 100);
    return `${+total.toFixed(2)} ${item.BaseUOM || ""}`;
  };

  // Close material search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".material-search-container")) {
        setActiveSearchIdx(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSave = (e) => {
    e.preventDefault();

    if (!formData.ActivityName.trim()) {
      toast.error("Activity Name is required");
      return;
    }
    if (!formData.Category) {
      toast.error("Please select a Category");
      return;
    }
    const invalidRow = lineItems.find((r) => !r.MaterialSKU || !r.Coefficient);
    if (invalidRow) {
      toast.error("Please select a material and enter a coefficient for each row.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        Id: isEdit ? Number(id) : null,
        ActivityName: formData.ActivityName.trim(),
        UOMBase: formData.UOMBase,
        Category: formData.Category,
        UnitEstimateRate: Number(formData.UnitEstimateRate) || 0,
        MaterialRows: lineItems,
      };

      saveRateLibraryRecord(payload);
      toast.success(
        isEdit
          ? "Rate Library entry updated successfully"
          : "Rate Library entry added successfully",
      );
      history.push("/works/rate-library");
    } catch (err) {
      toast.error("Failed to save Rate Library entry");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid px-3 pt-2">
      {/* ── Page Header ── */}
      <div className="row">
        <div className="col-md-12 text-left page_heading">
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="m-0">
              {isEdit ? "Update Rate Library Entry" : "Add Rate Library Entry"}
            </h4>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} autoComplete="off">
        {/* ── Header Fields ── */}
        <div className="row mb-2">
          <InputField
            label="Activity Name:"
            name="ActivityName"
            value={formData.ActivityName}
            onChange={handleFormChange}
            placeholder="e.g. PCC (1:4:8) Plain Cement Concrete"
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <SelectField
            label="UOM Base:"
            name="UOMBase"
            value={formData.UOMBase}
            onChange={handleFormChange}
            options={UOM_BASE_OPTIONS}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
          <SelectField
            label="Category:"
            name="Category"
            value={formData.Category}
            onChange={handleFormChange}
            options={categoryOptions}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
            <label className="input_label d-block m-0 mb-2">Unit Estimate Rate:</label>
            <div className="position-relative">
              <div
                className="input_styling ml-md-3 d-flex align-items-center"
                style={{
                  backgroundColor: "#f8f9fa",
                  fontWeight: "600",
                  color: "#17a2b8",
                }}
              >
                {formData.UnitEstimateRate
                  ? `PKR ${Number(formData.UnitEstimateRate).toLocaleString()} / ${formData.UOMBase}`
                  : `— / ${formData.UOMBase}`}
              </div>
              <small className="text-muted ml-md-3" style={{ fontSize: "10px" }}>
                (Standard benchmark calculation)
              </small>
            </div>
          </div>
        </div>

        {/* ── Section Title ── */}
        <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
          <h6 className="m-0" style={{ fontWeight: "600", color: "#333", fontSize: "14px" }}>
            Estimators define the expected materials
          </h6>
          <span style={{ fontSize: "12px", color: "#666" }}>Populated from Store Master</span>
        </div>

        {/* ── Line Items Table ── */}
        <div className="row mt-2 clearfix">
          <div className="col-md-12 p-0 column">
            <div className="table-responsive" style={{ overflow: "visible" }}>
              <table
                className="table table-bordered table-hover"
                id="tab_logic"
                style={{ fontSize: "13px" }}
              >
                <thead style={{ backgroundColor: "#E8FBFB" }}>
                  <tr style={{ height: "42px" }}>
                    <th className="text-center" style={{ ...headerCellStyle, width: 45 }}>
                      #
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "260px" }}>
                      Material SKU (from Store Master){" "}
                      <span className="estaric text-danger">*</span>
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "150px" }}>
                      Category
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "160px" }}>
                      Coefficient (Qty required) <span className="estaric text-danger">*</span>
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "110px" }}>
                      Base UOM
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "150px" }}>
                      Allowable Wastage (%) <span className="estaric text-danger">*</span>
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "160px" }}>
                      Standard Total incl. Wastage
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: 50 }}>
                      <span
                        onClick={handleAddRow}
                        style={{ cursor: "pointer" }}
                        title="Add Material Row"
                      >
                        <GrFormAdd className="add_icon drow" size="1.2em" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => {
                    const filteredMats = materialList.filter((m) => {
                      const q = (searchFilter[idx] || "").toLowerCase();
                      if (!q) {
                        return true;
                      }
                      return (
                        m.name.toLowerCase().includes(q) || m.category.toLowerCase().includes(q)
                      );
                    });

                    return (
                      <tr key={item.id || idx}>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          {idx + 1}
                        </td>

                        {/* Material Searchable Cell */}
                        <td style={{ verticalAlign: "middle" }}>
                          <div className="material-search-container position-relative">
                            <div className="d-flex align-items-center position-relative">
                              <input
                                type="text"
                                className="input_styling"
                                style={{ paddingRight: "30px", fontSize: "12px" }}
                                placeholder="Search material..."
                                value={
                                  activeSearchIdx === idx
                                    ? (searchFilter[idx] ?? "")
                                    : item.MaterialName
                                      ? `${item.MaterialName}`
                                      : ""
                                }
                                onFocus={() => {
                                  setActiveSearchIdx(idx);
                                  setSearchFilter((p) => ({
                                    ...p,
                                    [idx]: item.MaterialName ? `${item.MaterialName}` : "",
                                  }));
                                }}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setSearchFilter((p) => ({ ...p, [idx]: val }));
                                }}
                              />
                              <FiSearch
                                size={14}
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  color: "#888",
                                  pointerEvents: "none",
                                }}
                              />
                            </div>

                            {activeSearchIdx === idx && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  maxHeight: "220px",
                                  overflowY: "auto",
                                  backgroundColor: "#fff",
                                  border: "1px solid #ced4da",
                                  borderRadius: "4px",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                  zIndex: 9999,
                                }}
                              >
                                {filteredMats.length === 0 ? (
                                  <div
                                    style={{ padding: "8px 12px", fontSize: "12px", color: "#888" }}
                                  >
                                    No materials found
                                  </div>
                                ) : (
                                  filteredMats.map((m) => (
                                    <div
                                      key={m.sku}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelectMaterial(idx, m);
                                      }}
                                      style={{
                                        padding: "8px 12px",
                                        fontSize: "12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f1f1f1",
                                        transition: "background-color 0.15s ease",
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.currentTarget.style.backgroundColor = "#e8fbfb")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.currentTarget.style.backgroundColor = "transparent")
                                      }
                                    >
                                      <strong style={{ color: "#007bff" }}>{m.sku}</strong>
                                      <span style={{ marginLeft: "8px", color: "#333" }}>
                                        {m.name}
                                      </span>
                                      <span
                                        style={{
                                          marginLeft: "8px",
                                          fontSize: "11px",
                                          color: "#6c757d",
                                          backgroundColor: "#f8f9fa",
                                          padding: "1px 6px",
                                          borderRadius: "3px",
                                        }}
                                      >
                                        {m.category}
                                      </span>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Category (Auto-filled) */}
                        <td style={{ verticalAlign: "middle" }}>
                          <input
                            type="text"
                            className="input_styling"
                            value={item.MaterialCategory || ""}
                            readOnly
                            placeholder="—"
                            style={{ backgroundColor: "#f8f9fa", fontSize: "12px" }}
                          />
                        </td>

                        {/* Coefficient */}
                        <td style={{ verticalAlign: "middle" }}>
                          <input
                            type="number"
                            step="any"
                            className="input_styling text-center"
                            placeholder="e.g. 9.5"
                            value={item.Coefficient}
                            onChange={(e) => handleLineChange(idx, "Coefficient", e.target.value)}
                            required
                          />
                        </td>

                        {/* Base UOM (Auto-filled) */}
                        <td style={{ verticalAlign: "middle" }}>
                          <input
                            type="text"
                            className="input_styling text-center"
                            value={item.BaseUOM || ""}
                            readOnly
                            placeholder="—"
                            style={{ backgroundColor: "#f8f9fa", fontSize: "12px" }}
                          />
                        </td>

                        {/* Allowable Wastage (%) */}
                        <td style={{ verticalAlign: "middle" }}>
                          <input
                            type="number"
                            step="any"
                            className="input_styling text-center"
                            placeholder="0"
                            value={item.AllowableWastage}
                            onChange={(e) =>
                              handleLineChange(idx, "AllowableWastage", e.target.value)
                            }
                            required
                          />
                        </td>

                        {/* Standard Total incl Wastage */}
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle", fontWeight: "600", color: "#333" }}
                        >
                          {calculateTotalWithWastage(item)}
                        </td>

                        {/* Remove Action */}
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <span onClick={() => handleRemoveRow(idx)}>
                            <MdDeleteForever
                              size="1.3em"
                              style={{ cursor: "pointer", color: "#dc3545" }}
                              title="Remove"
                            />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Reconciliation Rule Note ── */}
        <div
          className="d-flex align-items-start mt-3 p-2"
          style={{
            backgroundColor: "#fcf8e3",
            border: "1px solid #faebcc",
            borderRadius: "4px",
            fontSize: "12px",
            color: "#8a6d3b",
          }}
        >
          <FiInfo size={16} style={{ marginRight: "8px", marginTop: "2px", flexShrink: 0 }} />
          <span>
            <strong>Reconciliation Rule:</strong> Actual site issues are reconciled against these
            benchmark quantities. Any variance exceeding the allowable wastage will trigger variance
            alerts during Bill of Quantities / Store consumption audits.
          </span>
        </div>

        {/* ── Footer Buttons ── */}
        <div className="float-right mt-4 mr-md-3 mb-5">
          <ActionButton
            text="Cancel"
            variant="cancel"
            onClick={() => history.push("/works/rate-library")}
          />
          <ActionButton
            text={isEdit ? "Update" : "Save"}
            icon={BsCloudUpload}
            type="submit"
            isLoading={isLoading}
          />
        </div>
      </form>
    </div>
  );
};

export { RateLibraryForm };
export default RateLibraryForm;
